import React from 'react';
import { Controller, useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { Checkbox, Input, InputNumber, Divider } from 'antd';
import { Info, Plus, Trash2, Eye } from 'react-feather';
import { Typography, IconButton, Button, Tooltip } from 'ui-kit';
import _ from 'lodash';
import './ProductPayoutSettings.scss';

export default function ProductPayoutSettings() {
  const { control, getValues } = useFormContext();

  const { product } = getValues();

  const amountSplits = useWatch({
    control,
    name: 'product.amountSplits',
    defaultValue: [],
  });

  const percentageSplits = useWatch({
    control,
    name: 'product.percentageSplits',
    defaultValue: [],
  });

  const {
    fields: recipientAmountFields,
    append: appendAmountRecipient,
    remove: removeAmountRecipient,
  } = useFieldArray({
    control,
    name: 'product.amountSplits',
  });

  const handleNewAmountRecipient = () => {
    appendAmountRecipient({
      recipient: '',
      amount: 0,
    });
  };

  const {
    fields: recipientPercentageFields,
    append: appendPercentageRecipient,
    remove: removePercentageRecipient,
  } = useFieldArray({
    control,
    name: 'product.percentageSplits',
  });

  const handleNewPercentageRecipient = () => {
    appendPercentageRecipient({
      recipient: '',
      percentage: 0,
    });
  };

  const totalRecipientAmount = _.sumBy(
    amountSplits,
    (item: { amount?: string | number }) => Number(item?.amount) || 0,
  );

  return (
    <div className="payout-modal">
      <div className="payout-modal__content">
        <div className="payout-modal__grid">
          <div className="payout-modal__form">
            <Controller
              control={control}
              name="product.newFieldRecipients"
              render={({ field: { value, onChange } }) => (
                <div className="payout-modal__toggle-box">
                  <div className="checkbox-group">
                    <Checkbox checked={value} onChange={onChange}>
                      <label>
                        Use store default payout split <span>(for recording only)</span>
                      </label>
                      <p>If toggled off, you can set a custom split for this product only.</p>
                    </Checkbox>
                  </div>
                </div>
              )}
            />

            <div className="payout-modal__banner">
              <Tooltip title="">
                <IconButton style={{ padding: 0 }}>
                  <Info size={16} />
                </IconButton>
              </Tooltip>
              <div className="banner-text">
                <strong>This split is for reporting and tracking only.</strong>
                <span>All payouts will go to the store&apos;s primary payout method.</span>
              </div>
            </div>

            <div className="payout-modal__section">
              <div className="section-header">
                <div className="title-group">
                  <span className="badge">1</span>
                  <div>
                    <Typography variant="h6">Wholesale cost</Typography>
                    <p>
                      Enter any fixed costs (e.g., vendor or service provider) associated with this
                      product.
                    </p>
                  </div>
                </div>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Plus size={14} />}
                  onClick={handleNewAmountRecipient}
                  className="add-btn"
                >
                  Add wholecost recipient
                </Button>
              </div>

              {recipientAmountFields.length > 0 && (
                <div className="list-labels">
                  <span className="label-recipient">Recipient</span>
                  <span className="label-value">Amount</span>
                </div>
              )}
              {recipientAmountFields.map((field, index) => (
                <div key={field.id} className="row-input">
                  <Controller
                    control={control}
                    name={`product.amountSplits.${index}.recipient`}
                    render={({ field }) => (
                      <Input {...field} placeholder="Recipient" className="text-input" />
                    )}
                  />

                  <div className="value-input-wrapper">
                    <span>$</span>
                    <Controller
                      control={control}
                      name={`product.amountSplits.${index}.amount`}
                      render={({ field }) => (
                        <InputNumber {...field} placeholder="0" bordered={false} />
                      )}
                    />
                  </div>

                  <button
                    type="button"
                    className="delete-btn"
                    onClick={() => removeAmountRecipient(index)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <div className="total-bar">
                <div className="label-group">
                  <Typography variant="h6">Total wholesale split</Typography>
                </div>
                <span className="total-value highlight">${totalRecipientAmount}</span>
              </div>
            </div>

            <div className="payout-modal__section">
              <div className="section-header">
                <div className="title-group">
                  <span className="badge">2</span>
                  <div>
                    <Typography variant="h6">Profit split</Typography>
                    <p>The remaining profit is split between recipients below.</p>
                  </div>
                </div>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Plus size={14} />}
                  onClick={handleNewPercentageRecipient}
                  className="add-btn"
                >
                  Add recipient
                </Button>
              </div>

              {recipientPercentageFields.length > 0 && (
                <div className="list-labels">
                  <span className="label-recipient">Recipient</span>
                  <span className="label-value">Percentage</span>
                </div>
              )}
              {recipientPercentageFields.map((field, index) => (
                <div key={field.id} className="row-input">
                  <Controller
                    control={control}
                    name={`product.percentageSplits.${index}.recipient`}
                    render={({ field }) => (
                      <Input {...field} placeholder="Recipient" className="text-input" />
                    )}
                  />

                  <div className="value-input-wrapper">
                    <Controller
                      control={control}
                      name={`product.percentageSplits.${index}.percentage`}
                      render={({ field }) => (
                        <InputNumber
                          {...field}
                          placeholder="0"
                          bordered={false}
                          min={0}
                          max={100}
                        />
                      )}
                    />
                    <span>%</span>
                  </div>

                  <button
                    type="button"
                    className="delete-btn"
                    onClick={() => removePercentageRecipient(index)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}

              <div className="total-bar">
                <div className="label-group">
                  <Typography variant="h6">Total profit split</Typography>
                </div>
                <span className="total-value highlight">100%</span>
              </div>
            </div>

            <div className="payout-modal__footer">
              <div className="footer-note">
                <Info size={16} style={{ flexShrink: 0 }} />
                <p>
                  This allocation is for your records and reporting only. All customer payments will
                  be paid out to your store&apos;s primary payout method.
                </p>
              </div>
            </div>
          </div>
          {/* Right */}
          <div className="payout-modal__preview">
            <div>
              <div className="preview-header">
                <Typography variant="h6">Allocation preview</Typography>
                <Eye size={16} />
              </div>

              <div className="preview-row bold">
                <Typography variant="body1" className="label">
                  Product price
                </Typography>
                <Typography variant="body2">${product.price}</Typography>
              </div>

              <Divider style={{ margin: '15px 0' }} />

              <div className="preview-group">
                <div className="preview-section-title">Wholesale cost</div>

                {amountSplits.map((item, index) => (
                  <div key={index} className="preview-row">
                    <span className="label">{item.recipient || ''}</span>
                    <span className="value">${Number(item.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
                <div className="preview-row bold">
                  <Typography variant="body1" className="label">
                    Profit to split
                  </Typography>
                  <Typography variant="body2">
                    ${Number(totalRecipientAmount).toFixed(2)}
                  </Typography>
                </div>
              </div>

              <Divider style={{ margin: '15px 0' }} />

              <div className="preview-group">
                <div className="preview-section-title">Profit split</div>

                {percentageSplits.map((item, index) => (
                  <div key={index} className="preview-row">
                    <span className="label">{item.recipient || ''}</span>
                    <span className="value">{Number(item.percentage || 0)}%</span>
                  </div>
                ))}
              </div>

              <Divider style={{ margin: '15px 0' }} />

              <div className="preview-total-box">
                <Typography variant="body1">Total allocated</Typography>
                <span className="amount">${Number(totalRecipientAmount).toFixed(2)}</span>
              </div>
            </div>

            <div className="preview-footer-note">
              <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <Typography variant="h6" style={{ fontSize: 11 }}>
                For reporting and tracking only.
                <br />
                All payouts go to your primary payout method.
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
